"use client";

import { type KeyboardEvent, useEffect, useRef, useState } from "react";
import {
  Checkbox,
  SelectField,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  HINT,
  INPUT_MONO,
  LABEL,
} from "@/components/tool-kit/styles";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";
import { countWords } from "@/lib/tool-kit/study/dictation";
import {
  clampWpm,
  formatClock,
  scrollSpeed,
  speakingSeconds,
} from "@/lib/tool-kit/study/teleprompter";

const COUNTDOWNS = ["0", "3", "5", "10"] as const;
const WPM_STEP = 10;
/** Where the reading line sits, as a share of the prompter's height. */
const GUIDE = 0.35;

type Countdown = (typeof COUNTDOWNS)[number];

/** English strings. A translated content file overrides these through `ui`. */
export const ONLINE_TELEPROMPTER_STRINGS = {
  scriptLabel: "Your script",
  scriptPlaceholder: "Paste or type what you're going to say.",
  loadSample: "Try a sample script",
  sampleScript:
    "Good afternoon, everyone. Over the next few minutes I'll walk you through what our group found when we tracked how much our dormitory throws away in a week.\n\nWe weighed every bag for seven days. The biggest surprise wasn't how much waste there was — it was how much of it could have been recycled.\n\nSo today I'll cover three things: what we measured, what we found, and the two changes we think would make the biggest difference.\n\nLet's start with how we measured it.",
  paceLabel: "Speaking pace (words per minute)",
  fontLabel: "Text size",
  fontValue: "{size} px",
  mirror: "Mirror the text (for teleprompter glass)",
  guide: "Show the reading line",
  countdownLabel: "Countdown before scrolling",
  countdowns: {
    "0": "None",
    "3": "3 seconds",
    "5": "5 seconds",
    "10": "10 seconds",
  },
  wordCount: "one:{count} word|other:{count} words",
  estimate: "{words}: about {time} at {wpm} words per minute",
  start: "Start",
  pause: "Pause",
  resume: "Resume",
  reset: "Back to top",
  slower: "Slower",
  faster: "Faster",
  fullScreen: "Full screen",
  exitFullScreen: "Exit full screen",
  paceShort: "{wpm} wpm",
  remaining: "{time} left",
  prompterLabel: "Teleprompter",
  emptyPrompter: "Your script will scroll here.",
  keysHint:
    "Click the prompter, or select it and press Space, to start and pause. ↑ and ↓ change the speed, Home goes back to the top, F toggles full screen and Esc leaves it.",
} as const;

export default function OnlineTeleprompterWidget() {
  const strings = useToolUi(ONLINE_TELEPROMPTER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [script, setScript] = useState("");
  const [wpmText, setWpmText] = useState("130");
  const [fontSize, setFontSize] = useState(44);
  const [mirror, setMirror] = useState(false);
  const [guide, setGuide] = useState(true);
  const [countdown, setCountdown] = useState<Countdown>("3");
  const [playing, setPlaying] = useState(false);
  const [counting, setCounting] = useState<number | null>(null);
  const [full, setFull] = useState(false);
  const [progress, setProgress] = useState(0);
  const [boxHeight, setBoxHeight] = useState(0);
  const stageRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLSpanElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const position = useRef(0);

  const words = countWords(script);
  const wpm = clampWpm(parseIntegerInput(wpmText) ?? 130);
  const total = speakingSeconds(words, wpm);
  const live = useRef({ words, wpm });
  live.current = { words, wpm };

  // The scroll loop: speed is re-read every frame, so pace changes apply mid-script.
  useEffect(() => {
    if (!playing) {
      return;
    }
    let frame = 0;
    let last = performance.now();
    let reported = 0;
    const tick = (now: number) => {
      const box = scrollRef.current;
      if (!box) {
        return;
      }
      const distance = box.scrollHeight - box.clientHeight;
      if (Math.abs(box.scrollTop - position.current) > 2) {
        position.current = box.scrollTop;
      }
      const speed = scrollSpeed(distance, live.current.words, live.current.wpm);
      position.current = Math.min(
        distance,
        position.current + (speed * (now - last)) / 1000
      );
      last = now;
      box.scrollTop = position.current;
      if (now - reported > 250) {
        reported = now;
        setProgress(distance > 0 ? position.current / distance : 1);
      }
      if (position.current >= distance) {
        setProgress(1);
        setPlaying(false);
        return;
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [playing]);

  // The countdown before scrolling starts.
  useEffect(() => {
    if (counting === null) {
      return;
    }
    if (counting === 0) {
      setCounting(null);
      setPlaying(true);
      return;
    }
    const timer = setTimeout(() => setCounting(counting - 1), 1000);
    return () => clearTimeout(timer);
  }, [counting]);

  // The padding that puts the first line on the reading line depends on the box height.
  useEffect(() => {
    const box = scrollRef.current;
    if (!box || typeof ResizeObserver === "undefined") {
      return;
    }
    const observer = new ResizeObserver(() => setBoxHeight(box.clientHeight));
    observer.observe(box);
    setBoxHeight(box.clientHeight);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const sync = () =>
      setFull(
        document.fullscreenElement === stageRef.current &&
          document.fullscreenElement !== null
      );
    document.addEventListener("fullscreenchange", sync);
    return () => document.removeEventListener("fullscreenchange", sync);
  }, []);

  function togglePlay() {
    if (playing || counting !== null) {
      setPlaying(false);
      setCounting(null);
      return;
    }
    if (words === 0) {
      return;
    }
    const box = scrollRef.current;
    if (box && box.scrollTop >= box.scrollHeight - box.clientHeight - 1) {
      position.current = 0;
      box.scrollTop = 0;
    }
    const seconds = Number(countdown);
    if (seconds > 0 && position.current === 0) {
      setCounting(seconds);
    } else {
      setPlaying(true);
    }
  }

  function reset() {
    setPlaying(false);
    setCounting(null);
    position.current = 0;
    setProgress(0);
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }

  function changePace(step: number) {
    setWpmText(String(clampWpm(wpm + step)));
  }

  function toggleFull() {
    const stage = stageRef.current;
    if (!stage) {
      return;
    }
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
      return;
    }
    if (stage.requestFullscreen) {
      stage.requestFullscreen().catch(() => setFull((current) => !current));
    } else {
      setFull((current) => !current);
    }
    buttonRef.current?.focus();
  }

  function handleKeys(event: KeyboardEvent<HTMLButtonElement>) {
    // Space and Enter click the button, which already starts and pauses.
    const actions: Record<string, () => void> = {
      ArrowUp: () => changePace(WPM_STEP),
      ArrowDown: () => changePace(-WPM_STEP),
      Home: reset,
      f: toggleFull,
      F: toggleFull,
      Escape: () => {
        if (!document.fullscreenElement) {
          setFull(false);
        }
      },
    };
    const action = actions[event.key];
    if (action) {
      event.preventDefault();
      action();
    }
  }

  let playLabel: string = strings.start;
  if (playing || counting !== null) {
    playLabel = strings.pause;
  } else if (progress > 0 && progress < 1) {
    playLabel = strings.resume;
  }
  const controls = (
    <div className="flex flex-wrap items-center gap-2">
      <button
        className={BUTTON_PRIMARY}
        disabled={words === 0}
        onClick={togglePlay}
        type="button"
      >
        {playLabel}
      </button>
      <button
        className={BUTTON_SECONDARY}
        onClick={() => changePace(-WPM_STEP)}
        type="button"
      >
        {strings.slower}
      </button>
      <span className="min-w-[5rem] text-center font-mono text-sm">
        {t(strings.paceShort, { wpm: fmt(wpm) })}
      </span>
      <button
        className={BUTTON_SECONDARY}
        onClick={() => changePace(WPM_STEP)}
        type="button"
      >
        {strings.faster}
      </button>
      <button className={BUTTON_SECONDARY} onClick={reset} type="button">
        {strings.reset}
      </button>
      <button className={BUTTON_SECONDARY} onClick={toggleFull} type="button">
        {full ? strings.exitFullScreen : strings.fullScreen}
      </button>
    </div>
  );

  return (
    <>
      <section className="space-y-3">
        <TextAreaField
          id="tp-script"
          label={strings.scriptLabel}
          onChange={setScript}
          placeholder={strings.scriptPlaceholder}
          prose
          rows={8}
          value={script}
        />
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p aria-live="polite" className={HINT}>
            {t(strings.estimate, {
              words: tPlural(strings.wordCount, locale, words, {
                count: fmt(words),
              }),
              time: formatClock(total),
              wpm: fmt(wpm),
            })}
          </p>
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setScript(strings.sampleScript)}
            type="button"
          >
            {strings.loadSample}
          </button>
        </div>
      </section>

      <section className="grid gap-4 sm:grid-cols-3 sm:items-end">
        <div className="space-y-2">
          <label className={LABEL} htmlFor="tp-wpm">
            {strings.paceLabel}
          </label>
          <input
            className={INPUT_MONO}
            id="tp-wpm"
            inputMode="numeric"
            onChange={(event) => setWpmText(event.target.value)}
            value={wpmText}
          />
        </div>
        <div className="space-y-2">
          <label className={LABEL} htmlFor="tp-size">
            {strings.fontLabel}: {t(strings.fontValue, { size: fmt(fontSize) })}
          </label>
          <input
            className="w-full accent-primary"
            id="tp-size"
            max={96}
            min={20}
            onChange={(event) => setFontSize(Number(event.target.value))}
            step={2}
            type="range"
            value={fontSize}
          />
        </div>
        <SelectField
          id="tp-countdown"
          label={strings.countdownLabel}
          onChange={setCountdown}
          options={COUNTDOWNS.map((value) => ({
            value,
            label: strings.countdowns[value],
          }))}
          value={countdown}
        />
        <div className="flex flex-wrap gap-4 sm:col-span-3">
          <Checkbox
            checked={mirror}
            label={strings.mirror}
            onChange={setMirror}
          />
          <Checkbox checked={guide} label={strings.guide} onChange={setGuide} />
        </div>
      </section>

      <section className="space-y-3">
        {controls}
        <div
          className={`${full ? "fixed inset-0 z-[70] flex flex-col" : "relative"} overflow-hidden rounded-lg bg-black text-white`}
          ref={stageRef}
        >
          <button
            aria-label={strings.prompterLabel}
            className={`relative block w-full cursor-pointer text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset ${full ? "flex-1" : "h-[60vh] min-h-[320px]"}`}
            onClick={togglePlay}
            onKeyDown={handleKeys}
            ref={buttonRef}
            type="button"
          >
            <span
              className="absolute inset-0 block overflow-y-auto [scrollbar-width:none]"
              ref={scrollRef}
            >
              <span
                className="mx-auto block max-w-4xl px-6 font-medium leading-snug"
                style={{
                  fontSize: `${fontSize}px`,
                  paddingTop: `${Math.round(boxHeight * GUIDE)}px`,
                  paddingBottom: `${Math.round(boxHeight * (1 - GUIDE))}px`,
                  transform: mirror ? "scaleX(-1)" : undefined,
                }}
              >
                <span className="block whitespace-pre-wrap">
                  {script.trim() || strings.emptyPrompter}
                </span>
              </span>
            </span>
            {guide ? (
              <span
                aria-hidden="true"
                className="pointer-events-none absolute inset-x-0 block border-primary/70 border-t-2"
                style={{ top: `${GUIDE * 100}%` }}
              />
            ) : null}
            {counting !== null ? (
              <span
                aria-live="assertive"
                className="absolute inset-0 flex items-center justify-center bg-black/60 font-bold text-[8rem]"
              >
                {fmt(counting)}
              </span>
            ) : null}
          </button>
          <div className="flex items-center justify-between gap-3 bg-black/80 px-4 py-2 text-sm text-white/80">
            <span>
              {t(strings.remaining, {
                time: formatClock(total * (1 - progress)),
              })}
            </span>
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-white/20">
              <div
                className="h-full bg-white/80"
                style={{ width: `${progress * 100}%` }}
              />
            </div>
            {full ? <div className="[&_button]:py-1">{controls}</div> : null}
          </div>
        </div>
        <p className={HINT}>{strings.keysHint}</p>
      </section>
    </>
  );
}
